import { Injectable, Injector } from '@angular/core';

import { Observable } from 'rxjs';

import { MentionsApi } from '@api/mentions/mentions.api';

import { LocalizationService } from '@Core/Localization/services/localization.service';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';
import { CurrencyConversionService } from '@Core/Users/services/currency-conversion.service';
import { IndexeddbCoreCacheService } from '@Core/Cache/services/indexeddb-core-cache.service';

import { SavedFilterService } from '@Common/Filter/services/saved-filter.service';
import { DiscoverListsFilterService } from '@Common/Filter/services/discover-lists-filter.service';
import { DataPointModel } from '@Common/Charts/models/data-point.model';

import { AbstractDashboardSearchService } from '@Monitor/Core/services/abstract-dashboard-search.service';

import { TagsService } from '../../Tags/services/tags.service';
import { TagGroupsService } from '../../Tags/services/tag-groups.service';
import { MonitorConfigService } from '../../MonitorShared/services/monitor-config.service';
import { JournodexRegionsService } from '../../Journodex/services/journodex-regions.service';
import { SourceGroupService } from '../../Sources/services/source-group.service';

import { ReportsSeriesMapperService } from './reports-series-mapper.service';
import { newDataProcessingWorkflowBlockTypes } from '../models/new-data-processing-workflow-block-types.model';
import { ReportsSeriesDataMapperService } from './reports-series-data-mapper.service';
import { FilterSectionEnum } from '@Common/Filter/enums/filter-section.enum';
import { FilterTypeCheckboxModel } from '@Common/Filter/models/filter-type-checkbox.model';
import { MediaSubscription } from '@Monitor/Shared/models/media-subscription.model';
import { MentionSearchQueryModel } from '@Monitor/Shared/models/mention-search-query.model';
import { SeriesFilterTypesEnum } from '../enums/series-filter-types.enum';
import { AgentService } from '@Social/Intelligence/services/agent.service';
import { FilterOptionModel } from '@Common/Filter/models/filter-option.model';
import { SeriesTypeEnum } from '../enums/series-type.enum';
import { FilterTypeModel } from '@Common/Filter/models/abstract-filter-type.model';


@Injectable()
export class ReportsDashboardSearchService extends AbstractDashboardSearchService {

    private agents: MediaSubscription[] = [];

    // Organization Section
    private readonly agentFilter = new FilterTypeCheckboxModel<MediaSubscription>(
        SeriesFilterTypesEnum.agents,
        'Monitoring.filtersLocalization.agents',
        [],
        false,
        undefined,
        undefined,
        undefined,
        undefined,
        FilterSectionEnum.ORGANIZATION
    );

    isThemeSource: boolean = false;

    private socialThemeFilters: FilterTypeModel[] = [
        this.agentFilter,
        ...this.allFilters
    ];

    constructor(
        protected tagsService: TagsService,
        protected tagGroupsService: TagGroupsService,
        protected journodexService: JournodexRegionsService,
        protected mentionsApi: MentionsApi,
        protected currentUserStateService: CurrentUserStateService,
        protected reportSeriesMapperService: ReportsSeriesMapperService,
        protected sourceGroupService: SourceGroupService,
        protected localizationService: LocalizationService,
        protected currencyConversionService: CurrencyConversionService,
        protected monitorConfigService: MonitorConfigService,
        protected savedFilterService: SavedFilterService,
        protected discoverListsFilterService: DiscoverListsFilterService,
        protected injector: Injector,
        protected cacheService: IndexeddbCoreCacheService,
        protected reportsSeriesDataMapperService: ReportsSeriesDataMapperService,
        private agentService: AgentService,
    ) {
        super(
            tagsService,
            tagGroupsService,
            journodexService,
            mentionsApi,
            currentUserStateService,
            reportSeriesMapperService,
            sourceGroupService,
            localizationService,
            currencyConversionService,
            monitorConfigService,
            savedFilterService,
            discoverListsFilterService,
            injector,
            cacheService
        );

        this.subscriptions.push(
            this.agentService.all$.subscribe(agents => this.agents = agents)
        );
    }

    handleReportsResponse(response, series) {
        let dataProcessingWorkflow: Observable<DataPointModel[]>;
        if (newDataProcessingWorkflowBlockTypes.includes(series.chartType)) {
            dataProcessingWorkflow = this.reportsSeriesDataMapperService.mapFacetValues(response, series);
        } else {
            dataProcessingWorkflow = this.reportSeriesMapperService.mapFacetValues(series, response);
        }
        dataProcessingWorkflow.subscribe({
            next: (data) => {
                if (series.mentions.isPreviousPeriod) {
                    series.previousPeriodDataProvider.next(data);
                } else {
                    // emit new data to charts
                    series.dataProvider.next(data);
                }
            },
            error: (error) => {
                // If we don't add an error case it shows a red error message in console
                series.dataProvider.error(error);
            }
        });
    }

    /**
     * Creates a search query from the current filters
     */
    generateFilterQuery(): MentionSearchQueryModel {
        const query = super.generateFilterQuery();
        if (this.filters.includes(this.agentFilter)) {
            query.agent.include = this.agentFilter.getCurrentValue().map(agent => agent.id);
        }
        return query;
    }

    populateFiltersFromSearchQuery(query: MentionSearchQueryModel, includeDate: boolean = true, setFilters: boolean = false) {
        super.populateFiltersFromSearchQuery(query, includeDate, setFilters);
        if (query.agent.include.length > 0) {
            const agents = this.agents.filter(agent => query.agent.include.includes(agent.id));
            this.agentFilter.setValue(agents);
        }

        if (setFilters) {
            if (this.isThemeSource) {
                this.setFilters(this.socialThemeFilters);
            } else {
                this.setFilters(this.allFilters);
            }
        }
    }

    /**
     * Applies the current filters onto the search
     */
    applyFilters(search: boolean = true): void {
        // we need to override this, because whe you do remove chip, we only do applyFilters, and the agentFilter is removed
        // // but the searchCriteria is not updated
        if (this.searchCriteria?.query?.agent && this.agentFilter?.getValue()?.length) {
            this.searchCriteria.query.agent.include = this.agentFilter.getValue().map(agent => agent.id);
        } else {
            if (this.isThemeSource) {
                this.searchCriteria.query.agent.include = [];
            }
        }
        super.applyFilters(search);
    }

    setAgentFilterOptions(themeId: number) {
        this.agentFilter.options = [];

        this.agents.forEach(agent => {
            if (agent.themeIds.includes(themeId)) {
                this.agentFilter.options.push(<FilterOptionModel>{
                    label: agent.name,
                    value: agent
                });
            }
        });
        this.agentFilter.options.sort((a, b) => a.label.localeCompare(b.label));
    }

    resetFilters(type: SeriesTypeEnum): void {
        switch (type) {
            case SeriesTypeEnum.theme:
                this.setFilters(this.socialThemeFilters);
                this.isThemeSource = true;
                break;
            default:
                this.isThemeSource = false;
                if (this.searchCriteria && !this.searchCriteria.isAccountSearch) {
                    this.setMercuryFilters();
                } else {
                    this.setAccountFilters(null);
                }
                break;
        }
    }

    public clearFilters(): void {
        if (this.isThemeSource) {
            this.socialThemeFilters.forEach(filt => filt.clear());
        }
        this.agentFilter.clear();
        super.clearFilters();
    }

    /**
     * Checks if there are filters that need to be re-applied after a new primary search is performed
     */
    protected hasFiltersToApply(): boolean {
        return super.hasFiltersToApply() || (this.agentFilter.chips.length > 0);
    }
}
